export interface DeviceProfile {
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isSafari: boolean;
  isStandalone: boolean;
  hasWebGPU: boolean;
  hasOffscreenCanvas: boolean;
  hasSharedArrayBuffer: boolean;
  hasWASMThreads: boolean;
  deviceMemoryGB?: number;
  hardwareConcurrency: number;
  /** Rough capability tier for client-side ML. */
  mlTier: 'low' | 'mid' | 'high';
}

export function detectDevice(): DeviceProfile {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isMobile = isIOS || isAndroid || /Mobi|Mobile/i.test(ua);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  const hasWebGPU = 'gpu' in navigator;
  const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  const hasSharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined';
  const hasWASMThreads = hasSharedArrayBuffer && typeof Atomics !== 'undefined';
  const deviceMemoryGB = (navigator as any).deviceMemory as number | undefined;
  const hardwareConcurrency = navigator.hardwareConcurrency ?? 4;

  let mlTier: DeviceProfile['mlTier'] = 'low';
  if (hasWebGPU && (deviceMemoryGB ?? 0) >= 6) mlTier = 'high';
  else if ((deviceMemoryGB ?? 0) >= 4 || hardwareConcurrency >= 6) mlTier = 'mid';

  return {
    isMobile, isIOS, isAndroid, isSafari, isStandalone,
    hasWebGPU, hasOffscreenCanvas, hasSharedArrayBuffer, hasWASMThreads,
    deviceMemoryGB, hardwareConcurrency, mlTier
  };
}

export const DEVICE = detectDevice();

/** True if a feature is expected to run smoothly on this device. */
export function canRunLocally(feature:
  | 'bg-remove-image'
  | 'bg-remove-video'
  | 'pose-landmarks'
  | 'motion-retarget-glb'
  | 'lipsync-precision'
  | 'ffmpeg-encode'
): boolean {
  const d = DEVICE;
  switch (feature) {
    case 'bg-remove-image': return true; // RMBG / BRIA runs everywhere
    case 'bg-remove-video': return d.mlTier !== 'low';
    case 'pose-landmarks': return true; // MediaPipe Pose works on mobile
    case 'motion-retarget-glb': return false; // requires server pipeline
    case 'lipsync-precision': return false; // requires server pipeline (audio-driven)
    case 'ffmpeg-encode': return d.hardwareConcurrency >= 4 && !d.isIOS; // iOS Safari struggles w/ wasm-threads
  }
}
